import { Link } from 'react-router-dom';
import { useAggregates } from '../lib/useAggregates';
import { usePerRegionAggregates } from '../lib/usePerRegionAggregates';
import { MapView } from '../components/MapView';
import { PartyStandings } from '../components/PartyStandings';
import { PopulationShare } from '../components/PopulationShare';
import { PlayerShare } from '../components/PlayerShare';
import { RecentFlips } from '../components/RecentFlips';
import { ContestedList } from '../components/ContestedList';
import { HowItWorks } from '../components/HowItWorks';
import { PulseStrip } from '../components/PulseStrip';
import { MapUpdateNotice } from '../components/MapUpdateNotice';
import { FunFacts } from '../components/FunFacts';
import { SpecialThanks } from '../components/SpecialThanks';
import { CountyControl } from '../components/CountyControl';
import { PowerBalance } from '../components/PowerBalance';
import { DownloadTiles } from '../components/DownloadTiles';

const nf = new Intl.NumberFormat('sv-SE');

const NAV_LINKS: { href: string; label: string; always?: boolean }[] = [
  { href: '#ladda-ner',     label: 'Ladda ner',     always: true },
  { href: '#karta',         label: 'Karta',         always: true },
  { href: '#sa-funkar-det', label: 'Så funkar det', always: true },
  { href: '#pulsen',        label: 'Pulsen' },
  { href: '#distrikt',      label: 'Distrikt' },
  { href: '#styrkelage',    label: 'Styrkeläge' },
  { href: '#lanen',         label: 'Länen' },
  { href: '#strider',       label: 'Strider' },
  { href: '#visste-du-att', label: 'Visste du' },
];

/** The 2026 retrospective is a separate page, not an anchor on this one. */
const VALET_2026_LINK = { to: '/valet-2026', label: 'Valet 2026' };

export function Home() {
  const state = useAggregates();
  const { valkretsar, countyAggregates, loaded } = usePerRegionAggregates();
  const hasStats = state.status === 'ready';

  return (
    <>
      {/* ── Hero (slim) ───────────────────────────────────────────── */}
      <section className="hero hero-slim">
        <img
          src="/icon.svg"
          alt="Partikampen logotyp"
          className="hero-icon"
        />
        <h1>Partikampen</h1>
        <p className="tagline">Erövra Sverige, ett distrikt i taget.</p>
        <p className="launch-badge">Tillgänglig nu på iOS &amp; Android</p>
      </section>

      {/* ── Sticky anchor nav ────────────────────────────────────── */}
      <nav className="section-nav" aria-label="Sidsektioner">
        <div className="section-nav-inner">
          {NAV_LINKS.filter(l => hasStats || l.always).map(link => (
            <a key={link.href} href={link.href}>{link.label}</a>
          ))}
          <Link to={VALET_2026_LINK.to}>{VALET_2026_LINK.label}</Link>
        </div>
      </nav>

      {/* ── Ladda ner ────────────────────────────────────────────── */}
      <section id="ladda-ner" className="install-section">
        <h2>Ladda ner appen</h2>
        <p className="install-desc">Gratis &middot; Tillgänglig på iOS och Android</p>
        <DownloadTiles />
      </section>

      {/* ── Karta ────────────────────────────────────────────────── */}
      <section id="karta" className="map-section">
        <h2>Karta</h2>
        {hasStats
          ? <MapView aggregate={state.data} valkretsar={valkretsar} countyAggregates={countyAggregates} aggregatesLoaded={loaded} />
          : <div className="map-skeleton" />
        }
        {hasStats && <MapUpdateNotice aggregate={state.data} />}
      </section>

      {/* ── Valet 2026 ───────────────────────────────────────────── */}
      {/* The retrospective is a separate page, so it needs an entry point in
          the body — the header and section-nav links are easy to miss. */}
      <Link to="/valet-2026" className="valet-banner">
        <div>
          <h2>Valet 2026</h2>
          <p>
            Valrörelsen är över. Se hur den slutade: striderna, kartan och
            siffrorna — och hur spelarna stod mot det riktiga valresultatet.
          </p>
        </div>
        <span className="valet-banner-cta" aria-hidden="true">›</span>
      </Link>

      {/* ── Så funkar det ────────────────────────────────────────── */}
      <HowItWorks />

      {/* ── Live stats ───────────────────────────────────────────── */}
      {hasStats && (
        <>
          <PulseStrip aggregate={state.data} />

          <section id="distrikt">
            <div className="stats-intro">
              <h2>Partistriderna just nu</h2>
              <p>
                {nf.format(
                  Object.values(state.data.totals.districtsByParty).reduce(
                    (a, b) => a + (b ?? 0),
                    0,
                  ),
                )}{' '}
                av{' '}
                {nf.format(state.data.totals.totalDistricts)} distrikt erövrade
              </p>
            </div>

            <div className="stats-grid">
              <PartyStandings totals={state.data.totals} />
              <PopulationShare totals={state.data.totals} />
              <PlayerShare totals={state.data.totals} />
            </div>
          </section>

          <section id="styrkelage">
            <PowerBalance aggregate={state.data} />
          </section>

          <CountyControl aggregate={state.data} />

          <section id="strider">
            <div className="flips-grid">
              <RecentFlips flips={state.data.recentFlips} />
              <ContestedList items={state.data.mostContested} />
            </div>
          </section>

          <FunFacts aggregate={state.data} />
        </>
      )}

      {state.status === 'loading' && (
        <p className="loading">Laddar statistik…</p>
      )}

      {state.status === 'ready' && state.demo && (
        <p className="demo-note">Exempeldata visas tills spelet lanseras.</p>
      )}

      <SpecialThanks />
    </>
  );
}
